import { useQuery, useQueryClient } from '@tanstack/react-query';
import clsx from 'clsx';
import { CalendarX, Copy, Plus, Trash2, X } from 'lucide-react';
import { useEffect, useState } from 'react';
import { toast } from '../../components/toasts';
import { Badge, Button, ErrorText, Field, Input, Select, Spinner } from '../../components/ui';
import { del, errorMessage, get, put } from '../../lib/api';
import { Team } from '../../lib/types';

interface TimeRange {
  start: string;
  end: string;
}
type Week = Record<string, TimeRange[]>;
interface SpecialDay {
  /** As que repetem todo ano vêm com o ano 2000. */
  date: string;
  recurring: boolean;
  closed: boolean;
  ranges: TimeRange[];
  description: string | null;
}
interface Overview {
  timeZone: string;
  general: { active: boolean; week: Week } | null;
  teams: Array<{ teamId: string; week: Week }>;
  specialDays: SpecialDay[];
  now: { open: boolean; alwaysOpen: boolean; nextOpening: string | null };
}

/** Segunda a domingo (0 = domingo no servidor). */
const DAYS: Array<{ key: string; label: string }> = [
  { key: '1', label: 'Segunda' },
  { key: '2', label: 'Terça' },
  { key: '3', label: 'Quarta' },
  { key: '4', label: 'Quinta' },
  { key: '5', label: 'Sexta' },
  { key: '6', label: 'Sábado' },
  { key: '0', label: 'Domingo' },
];
const DEFAULT_WEEK: Week = {
  '1': [{ start: '08:00', end: '18:00' }],
  '2': [{ start: '08:00', end: '18:00' }],
  '3': [{ start: '08:00', end: '18:00' }],
  '4': [{ start: '08:00', end: '18:00' }],
  '5': [{ start: '08:00', end: '18:00' }],
  '6': [],
  '0': [],
};
const MAX_RANGES = 3;
const clone = (w: Week): Week => Object.fromEntries(DAYS.map((d) => [d.key, (w[d.key] ?? []).map((r) => ({ ...r }))]));
const formatDate = (iso: string) => `${iso.slice(8, 10)}/${iso.slice(5, 7)}/${iso.slice(0, 4)}`;
/** Feriados nacionais de data fixa (os móveis, como Carnaval e Corpus Christi, mudam a cada ano). */
const NATIONAL_HOLIDAYS = [
  ['01-01', 'Confraternização Universal'],
  ['04-21', 'Tiradentes'],
  ['05-01', 'Dia do Trabalho'],
  ['09-07', 'Independência do Brasil'],
  ['10-12', 'Nossa Senhora Aparecida'],
  ['11-02', 'Finados'],
  ['11-15', 'Proclamação da República'],
  ['11-20', 'Dia Nacional de Zumbi e da Consciência Negra'],
  ['12-25', 'Natal'],
] as const;
const weekdayOf = (iso: string) => ['dom', 'seg', 'ter', 'qua', 'qui', 'sex', 'sáb'][new Date(`${iso}T12:00:00Z`).getUTCDay()];

/**
 * Horário de atendimento: semana geral, semana própria por equipe e datas especiais (feriados).
 * Fora do horário, o WhatsApp responde com a próxima abertura e a ligação vai para o aviso do PBX.
 */
export function HoursAdmin() {
  const qc = useQueryClient();
  const overview = useQuery({ queryKey: ['business-hours'], queryFn: () => get<Overview>('/business-hours') });
  const teams = useQuery({ queryKey: ['teams'], queryFn: () => get<Team[]>('/teams') });
  const refresh = () => void qc.invalidateQueries({ queryKey: ['business-hours'] });

  if (overview.isLoading) return <Spinner />;
  if (!overview.data) return <ErrorText error={overview.error} />;
  const o = overview.data;

  return (
    <div className="space-y-6 pb-8">
      <p className="max-w-3xl text-sm text-fg-soft">
        Fora do horário, o cliente do WhatsApp recebe a mensagem de <code>schedule.closed_message</code> (em Configurações) com a próxima abertura, e o atendimento fica na fila. Ligações de quem não está
        em atendimento vão para o aviso de fora do horário do PBX. Horários no fuso <b>{o.timeZone}</b>.
      </p>
      <GeneralSection overview={o} onSaved={refresh} />
      <TeamSection overview={o} teams={teams.data ?? []} onSaved={refresh} />
      <SpecialDaysSection days={o.specialDays} onSaved={refresh} />
    </div>
  );
}

function GeneralSection({ overview, onSaved }: { overview: Overview; onSaved: () => void }) {
  const [active, setActive] = useState(overview.general?.active ?? false);
  const [week, setWeek] = useState<Week>(clone(overview.general?.week ?? DEFAULT_WEEK));
  const [saving, setSaving] = useState(false);
  const now = overview.now;

  const save = async () => {
    setSaving(true);
    try {
      await put('/business-hours/general', { active, week });
      toast('Horário geral salvo', 'ok');
      onSaved();
    } catch (err) {
      toast(errorMessage(err), 'error');
    } finally {
      setSaving(false);
    }
  };

  return (
    <section className="rounded-lg border border-border bg-panel p-4">
      <div className="mb-3 flex flex-wrap items-center gap-3">
        <h2 className="font-semibold">Horário geral</h2>
        {now.alwaysOpen ? (
          <Badge>atende a qualquer hora</Badge>
        ) : now.open ? (
          <Badge tone="ok">aberto agora</Badge>
        ) : (
          <Badge tone="warn">fechado agora · abre {now.nextOpening}</Badge>
        )}
        <label className="ml-auto flex items-center gap-2 text-sm">
          <input type="checkbox" checked={active} onChange={(e) => setActive(e.target.checked)} />
          Usar horário de atendimento
        </label>
      </div>
      {!active && <p className="mb-2 text-xs text-fg-soft">Desligado: a plataforma atende a qualquer hora (o horário abaixo fica guardado).</p>}
      <div className={clsx(!active && 'opacity-50')}>
        <WeekEditor week={week} onChange={setWeek} />
      </div>
      <div className="mt-3 flex justify-end">
        <Button variant="primary" loading={saving} onClick={() => void save()}>
          Salvar horário geral
        </Button>
      </div>
    </section>
  );
}

function TeamSection({ overview, teams, onSaved }: { overview: Overview; teams: Team[]; onSaved: () => void }) {
  const [teamId, setTeamId] = useState('');
  const own = overview.teams.find((t) => t.teamId === teamId);
  const [week, setWeek] = useState<Week>(clone(DEFAULT_WEEK));
  const [saving, setSaving] = useState(false);
  const name = (id: string) => teams.find((t) => t.id === id)?.name ?? 'Equipe';

  useEffect(() => {
    setWeek(clone(own?.week ?? overview.general?.week ?? DEFAULT_WEEK));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [teamId]);

  const run = async (fn: () => Promise<unknown>, ok: string) => {
    setSaving(true);
    try {
      await fn();
      toast(ok, 'ok');
      onSaved();
    } catch (err) {
      toast(errorMessage(err), 'error');
    } finally {
      setSaving(false);
    }
  };

  return (
    <section className="rounded-lg border border-border bg-panel p-4">
      <h2 className="mb-1 font-semibold">Horário por equipe</h2>
      <p className="mb-3 text-sm text-fg-soft">Sem horário próprio, a equipe segue o geral. Feriados e datas especiais valem para todas.</p>
      {overview.teams.length > 0 && (
        <div className="mb-3 flex flex-wrap gap-1">
          {overview.teams.map((t) => (
            <button key={t.teamId} onClick={() => setTeamId(t.teamId)}>
              <Badge tone="accent">{name(t.teamId)}: horário próprio</Badge>
            </button>
          ))}
        </div>
      )}
      <Field label="Equipe">
        <Select value={teamId} onChange={(e) => setTeamId(e.target.value)} className="max-w-sm">
          <option value="">Escolha a equipe…</option>
          {teams.map((t) => (
            <option key={t.id} value={t.id}>
              {t.name}
              {overview.teams.some((o) => o.teamId === t.id) ? ' (horário próprio)' : ''}
            </option>
          ))}
        </Select>
      </Field>
      {teamId && (
        <>
          {!own && <p className="mb-2 text-xs text-fg-soft">Esta equipe segue o horário geral. Ajuste abaixo e salve para dar a ela um horário próprio.</p>}
          <WeekEditor week={week} onChange={setWeek} />
          <div className="mt-3 flex justify-end gap-2">
            {own && (
              <Button disabled={saving} onClick={() => void run(() => del(`/business-hours/teams/${teamId}`), 'A equipe voltou a seguir o horário geral')}>
                Voltar a seguir o geral
              </Button>
            )}
            <Button variant="primary" loading={saving} onClick={() => void run(() => put(`/business-hours/teams/${teamId}`, { week }), `Horário de ${name(teamId)} salvo`)}>
              Salvar horário da equipe
            </Button>
          </div>
        </>
      )}
    </section>
  );
}

function SpecialDaysSection({ days, onSaved }: { days: SpecialDay[]; onSaved: () => void }) {
  const [date, setDate] = useState('');
  const [closed, setClosed] = useState(true);
  const [recurring, setRecurring] = useState(false);
  const [ranges, setRanges] = useState<TimeRange[]>([{ start: '08:00', end: '12:00' }]);
  const [description, setDescription] = useState('');
  const [saving, setSaving] = useState(false);

  const add = async () => {
    if (!date) return;
    setSaving(true);
    try {
      await put(`/business-hours/special-days/${date}`, { closed, recurring, ranges: closed ? [] : ranges, description: description.trim() || undefined });
      toast('Data salva', 'ok');
      setDate('');
      setDescription('');
      onSaved();
    } catch (err) {
      toast(errorMessage(err), 'error');
    } finally {
      setSaving(false);
    }
  };

  /** Cadastra os feriados nacionais fixos que ainda não existem (repetem todo ano, fechado). */
  const addNational = async () => {
    const existing = new Set(days.filter((d) => d.recurring).map((d) => d.date.slice(5)));
    const missing = NATIONAL_HOLIDAYS.filter(([md]) => !existing.has(md));
    if (!missing.length) {
      toast('Os feriados nacionais fixos já estão cadastrados', 'info');
      return;
    }
    setSaving(true);
    try {
      for (const [md, name] of missing) await put(`/business-hours/special-days/2000-${md}`, { closed: true, recurring: true, description: name });
      toast(`${missing.length} feriado(s) nacional(is) cadastrado(s)`, 'ok');
      onSaved();
    } catch (err) {
      toast(errorMessage(err), 'error');
    } finally {
      setSaving(false);
    }
  };

  // Os que repetem todo ano primeiro (por dia e mês), depois as datas com ano.
  const sorted = [...days.filter((d) => d.recurring), ...days.filter((d) => !d.recurring)];

  const remove = async (d: string) => {
    try {
      await del(`/business-hours/special-days/${d}`);
      onSaved();
    } catch (err) {
      toast(errorMessage(err), 'error');
    }
  };

  return (
    <section className="rounded-lg border border-border bg-panel p-4">
      <h2 className="mb-1 font-semibold">Feriados e datas especiais</h2>
      <div className="mb-3 flex flex-wrap items-start justify-between gap-2">
        <p className="max-w-2xl text-sm text-fg-soft">
          Substituem o dia da semana para todos: fechado o dia inteiro ou com horário próprio (ex.: véspera de Natal até 12:00). Feriado fixo: marque <b>Repete todo ano</b> e cadastre uma vez só. Uma data
          com ano vale sobre a que repete (ex.: abrir meio período num Natal específico).
        </p>
        <Button size="sm" disabled={saving} onClick={() => void addNational()} title="Confraternização, Tiradentes, Trabalho, Independência, Aparecida, Finados, República, Consciência Negra e Natal">
          <Plus className="size-3.5" /> Feriados nacionais fixos
        </Button>
      </div>
      {days.length === 0 ? (
        <p className="mb-3 text-sm text-fg-soft">Nenhuma data cadastrada.</p>
      ) : (
        <div className="mb-4 divide-y divide-border rounded-md border border-border">
          {sorted.map((d) => (
            <div key={d.date} className="flex items-center gap-3 px-3 py-2 text-sm">
              <span className="w-32 font-mono">
                {d.recurring ? (
                  <>
                    {formatDate(d.date).slice(0, 5)} <span className="font-sans text-xs text-accent">todo ano</span>
                  </>
                ) : (
                  <>
                    {formatDate(d.date)} <span className="text-fg-soft">{weekdayOf(d.date)}</span>
                  </>
                )}
              </span>
              {d.closed ? (
                <Badge tone="warn">
                  <CalendarX className="size-3" /> fechado
                </Badge>
              ) : (
                <Badge tone="accent">{d.ranges.map((r) => `${r.start}–${r.end}`).join(' e ')}</Badge>
              )}
              <span className="flex-1 truncate text-fg-soft">{d.description}</span>
              <Button size="sm" variant="ghost" onClick={() => void remove(d.date)} aria-label={`Remover ${formatDate(d.date)}`}>
                <Trash2 className="size-3.5" />
              </Button>
            </div>
          ))}
        </div>
      )}
      <div className="flex flex-wrap items-end gap-3">
        <Field label="Data">
          <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="w-40" />
        </Field>
        <Field label="Descrição">
          <Input value={description} onChange={(e) => setDescription(e.target.value)} placeholder="Ex.: Natal" maxLength={100} className="w-56" />
        </Field>
        <label className="mb-2 flex items-center gap-2 text-sm">
          <input type="checkbox" checked={closed} onChange={(e) => setClosed(e.target.checked)} />
          Fechado o dia inteiro
        </label>
        <label className="mb-2 flex items-center gap-2 text-sm" title="Feriado fixo: vale todo ano no mesmo dia e mês">
          <input type="checkbox" checked={recurring} onChange={(e) => setRecurring(e.target.checked)} />
          Repete todo ano
        </label>
        {!closed && <RangesEditor ranges={ranges} onChange={setRanges} />}
        <Button variant="primary" className="mb-0.5" disabled={!date} loading={saving} onClick={() => void add()}>
          <Plus className="size-4" /> Adicionar data
        </Button>
      </div>
    </section>
  );
}

/** Semana: cada dia aberto (com até 3 faixas) ou fechado. */
function WeekEditor({ week, onChange }: { week: Week; onChange: (w: Week) => void }) {
  const set = (day: string, ranges: TimeRange[]) => onChange({ ...week, [day]: ranges });
  const copyMondayToWeekdays = () => {
    const monday = week['1'] ?? [];
    onChange({ ...week, ...Object.fromEntries(['2', '3', '4', '5'].map((d) => [d, monday.map((r) => ({ ...r }))])) });
  };
  return (
    <div>
      <div className="divide-y divide-border rounded-md border border-border">
        {DAYS.map((d) => {
          const ranges = week[d.key] ?? [];
          const open = ranges.length > 0;
          return (
            <div key={d.key} className="flex flex-wrap items-center gap-3 px-3 py-2">
              <label className="flex w-32 items-center gap-2 text-sm font-medium">
                <input type="checkbox" checked={open} onChange={(e) => set(d.key, e.target.checked ? [{ start: '08:00', end: '18:00' }] : [])} />
                {d.label}
              </label>
              {open ? <RangesEditor ranges={ranges} onChange={(r) => set(d.key, r)} /> : <span className="text-sm text-fg-soft">fechado</span>}
            </div>
          );
        })}
      </div>
      <Button size="sm" variant="ghost" className="mt-1" onClick={copyMondayToWeekdays}>
        <Copy className="size-3.5" /> Copiar segunda para terça a sexta
      </Button>
    </div>
  );
}

function RangesEditor({ ranges, onChange }: { ranges: TimeRange[]; onChange: (r: TimeRange[]) => void }) {
  const update = (i: number, patch: Partial<TimeRange>) => onChange(ranges.map((r, j) => (j === i ? { ...r, ...patch } : r)));
  return (
    <div className="flex flex-wrap items-center gap-2">
      {ranges.map((r, i) => (
        <span key={i} className="flex items-center gap-1 rounded-md bg-muted px-1.5 py-1">
          <Input type="time" value={r.start} onChange={(e) => update(i, { start: e.target.value })} className="h-7 w-[6.5rem] text-xs" aria-label="Início" />
          <span className="text-xs text-fg-soft">às</span>
          <Input type="time" value={r.end} onChange={(e) => update(i, { end: e.target.value })} className="h-7 w-[6.5rem] text-xs" aria-label="Fim" />
          {ranges.length > 1 && (
            <button className="rounded p-0.5 text-fg-soft hover:bg-panel" onClick={() => onChange(ranges.filter((_, j) => j !== i))} aria-label="Remover faixa">
              <X className="size-3.5" />
            </button>
          )}
        </span>
      ))}
      {ranges.length < MAX_RANGES && (
        <Button
          size="sm"
          variant="ghost"
          onClick={() => {
            const last = ranges.at(-1)?.end ?? '08:00';
            onChange([...ranges, { start: last, end: last < '23:00' ? `${String(Number(last.slice(0, 2)) + 1).padStart(2, '0')}:${last.slice(3)}` : '23:59' }]);
          }}
          title="Acrescentar faixa (ex.: depois do almoço)"
        >
          <Plus className="size-3.5" /> faixa
        </Button>
      )}
    </div>
  );
}
